// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * Policy attached to an action (e.g. confirmation required).
 */
export const zActionPolicy = z.object({
    requiresConfirmation: z.boolean().nullish()
});

/**
 * A single OHLCV bar. `time` is a unix timestamp in seconds.
 */
export const zCandle = z.object({
    close: z.number(),
    high: z.number(),
    low: z.number(),
    open: z.number(),
    time: z.number(),
    volume: z.number()
});

/**
 * A component (or widget placement) placed on a free-form canvas.
 *
 * Coordinates and sizes are in logical canvas units; the frontend canvas layer scales them to the
 * viewport. `z_index` is a placement concern (not a component property) because the same
 * component or widget may be placed more than once.
 */
export const zCanvasItem = z.object({
    h: z.number(),
    minH: z.number().nullish(),
    minW: z.number().nullish(),
    pinned: z.boolean().nullish(),
    w: z.number(),
    widget: z.string(),
    x: z.number(),
    y: z.number(),
    zIndex: z.int().min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' }).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish()
});

/**
 * Minimum runtime version / capabilities a page requires.
 */
export const zCompatibility = z.object({
    minRuntimeVersion: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish(),
    requiredCapabilities: z.array(z.string()).nullish()
});

/**
 * `POST /data-sessions` response: the session id plus the transport URLs
 * that belong to it (spec §3.1). Bulk/live endpoints arrive with items 1.4 /
 * 1.5; the URLs are issued up front so the client topology does not change.
 */
export const zDataSessionCreated = z.object({
    bulkUrl: z.string(),
    liveUrl: z.string(),
    requirementsUrl: z.string(),
    sessionId: z.uuid()
});

/**
 * Operational light status of a session (`GET …/status`).
 */
export const zDataSessionStatus = z.object({
    currentRevision: z.number(),
    lastSeenAt: z.string(),
    sessionId: z.uuid(),
    state: z.string()
});

/**
 * Kind of external dataset backing a page.
 *
 * 1:1 with the market-data catalog's `catalog.dataset.shape` vocabulary
 * (migration 0008): ATOM | STREAM | BOARD.
 *
 * - `Series` ↔ ATOM — scalar observations with independent longitudinal
 * meaning, one `measure` per series (an OHLCV chart consumes the open/
 * high/low/close/volume series over one instrument — a candle is a stitched
 * view, not a stored row type). Canonical values are FIXED-POINT Int64
 * scaled by `scale_factor ∈ [0, 18]`; the data layer decodes them to f64 at
 * the delivery boundary — the runtime never sees raw integers.
 * - `Stream` ↔ STREAM — ordered structured discrete occurrences (trades,
 * news, book deltas) in one shared event table; each stream carries an
 * `event_family` + versioned `payload_schema` for envelope decoding.
 * - `Board` ↔ BOARD — a derived/read-model of state at an `as_of` time. The
 * catalog enforces that canonical datasets are never boards (boards are
 * always DERIVED), so a manifest may only consume boards as read-models.
 */
export const zDatasetKind = z.enum([
    'series',
    'stream',
    'board'
]);

/**
 * A horizontal/vertical direction for `Stack`/`Split`.
 */
export const zDirection = z.enum(['horizontal', 'vertical']);

/**
 * Where a derivation executes.
 */
export const zExecutor = z.enum([
    'main',
    'worker',
    'auto'
]);

/**
 * A component (or widget placement) placed in a grid cell.
 */
export const zGridItem = z.object({
    h: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
    w: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
    widget: z.string(),
    x: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
    y: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
    zIndex: z.int().min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' }).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish()
});

export const zJsonValue = z.unknown();

/**
 * An explicit, imperative side effect.
 */
export const zActionDefinition = z.object({
    inputs: zJsonValue.optional(),
    policy: zActionPolicy.nullish(),
    type: z.string()
});

/**
 * A pure computation consuming references/literals and producing a value.
 */
export const zDerivationDefinition = z.object({
    executor: zExecutor.nullish(),
    inputs: zJsonValue.optional(),
    operation: z.string(),
    operationVersion: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish(),
    parameters: zJsonValue.optional()
});

export const zLatestQuery = z.object({
    symbol: z.string()
});

export const zPageRequestQuery = z.object({
    route: z.string(),
    runtimeVersion: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish(),
    view: z.string().nullish(),
    widgets: z.string().nullish()
});

/**
 * How a page is laid out / how widgets may move.
 *
 * `Grid` is the legacy structured CSS-grid flow and is the default when the field is absent.
 * `CanvasGrid` / `Canvas` are free-form canvases rendered by the frontend canvas layer;
 * `CanvasGrid` is magnetic (snaps to grid markers) while `Canvas` is unconstrained.
 */
export const zPageRigidity = z.enum([
    'grid',
    'canvas_grid',
    'canvas'
]);

/**
 * Page identity and compatibility requirements.
 */
export const zPageDefinition = z.object({
    compatibility: zCompatibility.nullish(),
    description: z.string().nullish(),
    id: z.string(),
    rigidity: zPageRigidity.nullish(),
    title: z.string().nullish()
});

/**
 * A synthetic latest quote.
 */
export const zQuote = z.object({
    change: z.number(),
    changePct: z.number(),
    price: z.number(),
    symbol: z.string(),
    ts: z.number()
});

/**
 * The `date_range` bound payload (RFC 3339 instants; both optional on the
 * wire, at least one required after validation).
 */
export const zRangeBound = z.object({
    from: z.string().nullish(),
    to: z.string().nullish()
});

/**
 * One bound on a requirement (spec §5). Limits combine as logical AND. The
 * wire shape is exactly the spec's: `{"type":"count","limit":100}` and
 * `{"type":"date_range","limit":{"from":…,"to":…}}`.
 */
export const zDataLimit = z.union([
    z.object({
        limit: z.number(),
        type: z.enum(['count'])
    }),
    z.object({
        limit: zRangeBound,
        type: z.enum(['date_range'])
    })
]);

/**
 * One required logical data item with its bounds (spec §5).
 */
export const zRequirement = z.object({
    data: z.string(),
    limits: z.array(zDataLimit).optional(),
    live: z.boolean(),
    resumeFromAsOf: z.number().nullish()
});

/**
 * `POST /data-sessions/{id}/requirements` response: the ack (DR-1). Returned
 * only after the requirement set is durably committed to Postgres.
 * `plan_version` is currently ALWAYS `null`: the planner (item 1.6) versions
 * plans internally, but the ack path deliberately never waits for planning
 * (the replan is fire-and-forget), so it has no version to report and
 * surfaces `None` on both the fresh-ack and stale/no-op paths.
 */
export const zRequirementsAccepted = z.object({
    acceptedRevision: z.number(),
    planVersion: z.number().nullish()
});

/**
 * The terminal's small explicit runtime type system.
 *
 * Every graph node (state, signal, dataset, derivation output) and every operation/widget
 * input/output is typed with one of these. The frontend compiler type-checks wiring at compile
 * time (e.g. a `TimeRange` fed to a `Number` slot fails compilation). `Nullable<T>` makes
 * empty/optional interactive state first-class.
 *
 * Rust is the single source of truth: this enum is emitted to OpenAPI and generates the
 * TypeScript discrimination the compiler uses.
 */
export const zRuntimeType = z.union([
    z.object({
        type: z.enum(['null'])
    }),
    z.object({
        type: z.enum(['number'])
    }),
    z.object({
        type: z.enum(['string'])
    }),
    z.object({
        type: z.enum(['boolean'])
    }),
    z.object({
        type: z.enum(['timestamp'])
    }),
    z.object({
        type: z.enum(['time_range'])
    }),
    z.object({
        type: z.enum(['entity_ref'])
    }),
    z.object({
        type: z.enum(['series_ref'])
    }),
    z.object({
        type: z.enum(['table_ref'])
    }),
    z.object({
        type: z.enum(['board_ref'])
    }),
    z.object({
        type: z.enum(['point_selection'])
    }),
    z.object({
        element: z.lazy((): any => zRuntimeType),
        type: z.enum(['selection_set'])
    }),
    z.object({
        element: z.lazy((): any => zRuntimeType),
        type: z.enum(['array'])
    }),
    z.object({
        inner: z.lazy((): any => zRuntimeType),
        type: z.enum(['nullable'])
    }),
    z.object({
        type: z.enum(['json'])
    })
]);

/**
 * A persistent external dataset (e.g. AAPL close series) consumed by widgets/derivations.
 *
 * The manifest only declares the dataset's *semantic shape*; the frontend data layer (Data
 * Client / Data Worker) resolves and streams the actual data into a runtime cell addressed as
 * `$datasets.<id>`.
 */
export const zDatasetDefinition = z.object({
    kind: zDatasetKind.nullish(),
    source: zJsonValue.optional(),
    type: zRuntimeType
});

export const zSeriesQuery = z.object({
    bars: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish(),
    interval: z.string().nullish(),
    symbol: z.string()
});

/**
 * A series of bars for a symbol/interval.
 */
export const zSeriesResponse = z.object({
    bars: z.array(zCandle),
    interval: z.string(),
    symbol: z.string()
});

/**
 * Whether a signal is transient (hover/cursor, high frequency) or committed (selection).
 */
export const zSignalMode = z.enum(['transient', 'committed']);

/**
 * A small reactive page value.
 */
export const zSignalDefinition = z.object({
    initial: zJsonValue.optional(),
    mode: zSignalMode.nullish(),
    type: zRuntimeType
});

/**
 * Where a page-state value persists (or not).
 */
export const zStatePersistence = z.enum([
    'none',
    'session',
    'user'
]);

/**
 * A mutable, page-scoped piece of application state.
 */
export const zStateDefinition = z.object({
    initial: zJsonValue.optional(),
    persistence: zStatePersistence.nullish(),
    type: zRuntimeType
});

/**
 * `POST /data-sessions/{id}/requirements` body: the COMPLETE desired set
 * with a monotonically increasing revision (spec §7, DR-1).
 */
export const zSubmitRequirements = z.object({
    requirements: z.array(zRequirement),
    revision: z.number()
});

/**
 * A `$`-prefixed path reference such as `$state.selected_point` or `$derivations.return_pct`.
 *
 * A newtype (not a `String` alias) so `utoipa` emits a distinct schema name. On the wire it
 * serializes as a plain string; the frontend compiler resolves it to a node id.
 */
export const zValueReference = z.string();

/**
 * A component instance, keyed by a semantic type + version.
 *
 * A *component* is the atomic, frontend-defined unit (the "lego brick"): `price_chart`, `text`,
 * `metric`, … The user never places a component directly — they place a **widget**, which is a
 * composition of components (see [`WidgetLibraryEntry`]).
 */
export const zComponentDefinition = z.object({
    bindings: zJsonValue.optional(),
    capabilities: z.record(z.string(), z.boolean()).nullish(),
    outputs: z.record(z.string(), zValueReference).nullish(),
    props: zJsonValue.optional(),
    type: z.string(),
    version: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' })
});

/**
 * A widget placed on a page. `widget` names a widget-library entry (stable `key`); `inputs`
 * supplies a value (literal or `$...` page reference) for each declared input field.
 */
export const zWidgetPlacement = z.object({
    inputs: zJsonValue.optional(),
    version: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish(),
    widget: z.string()
});

/**
 * High-level layout composition of components/widgets.
 *
 * Semantic primitives only — the frontend design system interprets these; the manifest
 * never carries low-level CSS/DOM.
 */
export const zLayoutDefinition = z.union([
    z.object({
        children: z.array(zGridItem),
        columns: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
        rows: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).nullish(),
        type: z.enum(['grid'])
    }),
    z.object({
        children: z.array(zCanvasItem),
        grid: z.number().nullish(),
        type: z.enum(['canvas'])
    }),
    z.object({
        children: z.array(z.lazy((): any => zLayoutDefinition)),
        direction: zDirection.nullish(),
        type: z.enum(['stack'])
    }),
    z.object({
        direction: zDirection,
        first: z.lazy((): any => zLayoutDefinition),
        ratio: z.number().nullish(),
        second: z.lazy((): any => zLayoutDefinition),
        type: z.enum(['split'])
    }),
    z.object({
        children: z.array(z.lazy((): any => zTabDefinition)),
        type: z.enum(['tabs'])
    }),
    z.object({
        type: z.enum(['widget']),
        widget: z.string()
    })
]);

/**
 * Versioned, declarative description of a page.
 *
 * This is the contract between backend product composition and frontend execution. It is a
 * semantic DSL, never arbitrary JavaScript/JSX/HTML/CSS.
 */
export const zPageManifest = z.object({
    actions: z.record(z.string(), zActionDefinition).nullish(),
    components: z.record(z.string(), zComponentDefinition).optional(),
    datasets: z.record(z.string(), zDatasetDefinition).nullish(),
    derivations: z.record(z.string(), zDerivationDefinition).nullish(),
    layout: zLayoutDefinition,
    page: zPageDefinition,
    schemaVersion: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
    signals: z.record(z.string(), zSignalDefinition).nullish(),
    state: z.record(z.string(), zStateDefinition).nullish(),
    widgets: z.record(z.string(), zWidgetPlacement).optional()
});

/**
 * A named tab wrapping a sub-layout.
 */
export const zTabDefinition = z.object({
    content: zLayoutDefinition,
    title: z.string()
});

/**
 * A widget's internal composition: a layout plus the component instances it references. This is
 * a miniature page — component ids are local to the widget and namespaced on expansion.
 */
export const zWidgetComposition = z.object({
    components: z.record(z.string(), zComponentDefinition),
    layout: zLayoutDefinition
});

/**
 * A widget-library entry as stored in Postgres and served by `GET /api/v1/widgets`.
 *
 * `(key, version)` is unique. The composition may refer to `$inputs.<field>` anywhere a `$...`
 * reference is accepted; the client substitutes the placement's input values at compile time.
 */
export const zWidgetLibraryEntry = z.object({
    category: z.string().nullish(),
    composition: zWidgetComposition,
    description: z.string().nullish(),
    inputSchema: z.record(z.string(), zRuntimeType).optional(),
    key: z.string(),
    outputSchema: z.record(z.string(), zRuntimeType).nullish(),
    title: z.string(),
    version: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' })
});

/**
 * Session registered
 */
export const zRegisterSessionResponse = zDataSessionCreated;

export const zCloseSessionPath = z.object({
    sessionId: z.uuid()
});

/**
 * Session closed (row deleted, requirement set cascaded)
 */
export const zCloseSessionResponse = z.void();

export const zSubmitRequirementsBody = zSubmitRequirements;

export const zSubmitRequirementsPath = z.object({
    sessionId: z.uuid()
});

/**
 * Desired state acked (or a stale/duplicate revision: idempotent no-op with the current accepted revision)
 */
export const zSubmitRequirementsResponse = zRequirementsAccepted;

export const zSessionStatusPath = z.object({
    sessionId: z.uuid()
});

/**
 * Session status
 */
export const zSessionStatusResponse = zDataSessionStatus;

export const zLatestQuery2 = z.object({
    symbol: z.string()
});

/**
 * Synthetic latest quote
 */
export const zLatestResponse = zQuote;

export const zSeriesQuery2 = z.object({
    symbol: z.string(),
    interval: z.string().optional(),
    bars: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).optional()
});

/**
 * Synthetic series
 */
export const zSeriesResponse2 = zSeriesResponse;

export const zResolvePageQuery = z.object({
    route: z.string(),
    view: z.string().optional(),
    runtimeVersion: z.int().gte(0).max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }).optional(),
    widgets: z.string().optional()
});

/**
 * Server-composed page manifest
 */
export const zResolvePageResponse = zPageManifest;
